import React from 'react';
import { useParams } from 'react-router-dom';
import { useAccessGroups } from './ResourcesRouter.js';
import {
	Card,
	CardBody,
	Button,
	Input,
	Skeleton,
	addToast
} from '@heroui/react';
import { Fuel, Save, Plus, Trash2, Upload, X } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import { UnsavedChangesGuard, useUnsavedChangesGuard } from './components/UnsavedChangesGuard.js';

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Empty graph row template
const emptyGraphRow = () => ({
	load: '',
	weight: '',
	arm: ''
});

function FuelTankPageContent() {
	const { aircraftId } = useParams();
	const { setDirty, navigateWithCheck } = useUnsavedChangesGuard();
	const accessGroups = useAccessGroups();
	const canManage = accessGroups._loaded && !!accessGroups.resourcesManage;
	const orgUnique = getOrgUnique();

	const [aircraft, setAircraft] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [saving, setSaving] = React.useState(false);

	// Fuel tank form state
	const [tankModel, setTankModel] = React.useState('');
	const [graphData, setGraphData] = React.useState([]);

	// File input ref
	const fileInputRef = React.useRef(null);

	// Fetch aircraft and fuel tank data
	const fetchData = React.useCallback(async () => {
		if (!aircraftId) return;
		setLoading(true);
		try {
			// Fetch aircraft data
			const aircraftResponse = await ResourcesApi.getAircraft(parseInt(aircraftId, 10));
			const aircraftData = aircraftResponse.data?.data || aircraftResponse.data || aircraftResponse;
			setAircraft(aircraftData);

			// Fetch fuel tank data and resolve model name
			try {
				const [fuelTankResponse, allTanksResponse] = await Promise.all([
					ResourcesApi.getFuelTank(parseInt(aircraftId, 10)),
					ResourcesApi.getAllFuelTanks()
				]);
				const tankData = fuelTankResponse.data?.data || fuelTankResponse.data || {};
				const allTanks = allTanksResponse.data?.data || allTanksResponse.data || [];

				// Resolve model name: try API response first, then match by slidingFuelTankId from W&B data
				let modelName = tankData.model || tankData.tankModel || '';
				if (!modelName) {
					const wb = aircraftData.weightAndBalance || aircraftData.weight_and_balance || {};
					const slidingTank = wb.slidingFuelTank || aircraftData.slidingFuelTank;
					const slidingId = wb.slidingFuelTankId || aircraftData.slidingFuelTankId
						|| (slidingTank?.id) || (typeof slidingTank === 'number' ? slidingTank : null)
						|| tankData.id || tankData.fuelTankId;
					if (slidingId && Array.isArray(allTanks)) {
						const matchedTank = allTanks.find(t => t.id === slidingId);
						if (matchedTank) modelName = matchedTank.model || '';
					}
				}
				setTankModel(modelName);

				const graph = tankData.graph || tankData.fuelTankGraph || [];
				setGraphData(graph.length > 0 ? graph.map(row => ({
					load: row.load || '',
					weight: row.weight || '',
					arm: row.arm || ''
				})) : [emptyGraphRow()]);
			} catch (tankErr) {
				// Initialize with empty data if no fuel tank data
				console.log('No existing fuel tank data, initializing empty');
				setTankModel('');
				setGraphData([emptyGraphRow()]);
			}
		} catch (err) {
			console.error('Failed to load data:', err);
			addToast({
				title: 'Error',
				description: 'Failed to load aircraft data',
				color: 'danger'
			});
		} finally {
			setLoading(false);
		}
	}, [aircraftId]);

	React.useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Handle graph row changes
	const handleGraphChange = (index, field, value) => {
		setGraphData(prev => {
			const newData = [...prev];
			newData[index] = { ...newData[index], [field]: value };
			return newData;
		});
		setDirty(true);
	};

	// Add new row
	const handleAddRow = () => {
		setGraphData(prev => [...prev, emptyGraphRow()]);
		setDirty(true);
	};

	// Remove row
	const handleRemoveRow = index => {
		setGraphData(prev => prev.filter((_, i) => i !== index));
		setDirty(true);
	};

	// Handle file upload
	const handleFileUpload = async event => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Check file type
		const validTypes = [
			'text/csv',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		];
		if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xls|xlsx)$/i)) {
			addToast({
				title: 'Invalid File',
				description: 'Please upload a CSV, XLS, or XLSX file',
				color: 'danger'
			});
			return;
		}

		// Read CSV file
		if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
			const reader = new FileReader();
			reader.onload = e => {
				try {
					const text = e.target?.result;
					const lines = text.split('\n').filter(line => line.trim());
					const newData = [];

					for (const line of lines) {
						const values = line.split(',').map(v => v.trim());
						if (values.length >= 3) {
							const load = parseFloat(values[0]);
							const weight = parseFloat(values[1]);
							const arm = parseFloat(values[2]);

							if (!isNaN(load) && !isNaN(weight) && !isNaN(arm)) {
								newData.push({
									load: String(load),
									weight: String(weight),
									arm: String(arm)
								});
							}
						}
					}

					if (newData.length > 0) {
						setGraphData(newData);
						addToast({
							title: 'File Imported',
							description: `Imported ${newData.length} rows from CSV`,
							color: 'success'
						});
					} else {
						addToast({
							title: 'No Data',
							description: 'No valid data found in file. Expected 3 columns: Load (%), Weight (lbs), Arm (in)',
							color: 'warning'
						});
					}
				} catch (parseErr) {
					addToast({
						title: 'Parse Error',
						description: 'Failed to parse the uploaded file',
						color: 'danger'
					});
				}
			};
			reader.readAsText(file);
		} else {
			// For Excel files, show info message
			addToast({
				title: 'Excel Upload',
				description: 'Excel file upload requires server-side processing. Please use CSV format or enter data manually.',
				color: 'warning'
			});
		}

		// Reset file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	// Save fuel tank data
	const handleSave = async () => {
		setSaving(true);
		try {
			// Prepare data for API — backend expects only { graph: [...] }
			const payload = {
				graph: graphData.map(row => ({
					load: String(row.load || '0'),
					weight: String(row.weight || '0'),
					arm: String(row.arm || '0')
				}))
			};

			await ResourcesApi.saveFuelTankGraph(parseInt(aircraftId, 10), payload);
			setDirty(false);
			addToast({
				title: 'Success',
				description: 'Fuel tank data saved successfully',
				color: 'success'
			});
		} catch (err) {
			console.error('Failed to save:', err);
			const status = err.response?.status;
			const description = status === 422
				? 'Please fill in all fields before saving'
				: err.message || 'Failed to save fuel tank data';
			addToast({
				title: 'Error',
				description,
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Back navigation
	const handleBack = () => {
		navigateWithCheck(`/aircraft/${aircraftId}/weight-balance`);
	};

	// Breadcrumbs
	const breadcrumbCustomItems = [
		{ label: window.location.pathname.includes('/hangars') ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${window.location.pathname.includes('/hangars') ? 'hangars' : 'resources'}/` },
		{ label: aircraft?.name || 'Aircraft', href: `/org/${orgUnique}/resources/aircraft/${aircraftId}` }
	];

	// Loading skeleton
	if (loading) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<Skeleton className="h-24 w-full rounded-lg mb-4" />
				<Skeleton className="h-64 w-full rounded-lg" />
			</div>
		);
	}

	return (
		<div className="max-w-4xl mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="Fuel Tank" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600">
						<Fuel className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{aircraft?.name || 'Aircraft'}</h1>
						<p className="text-default-500 text-sm">
							{aircraft?.registrationNumber}
							{(aircraft?.manufacturer || aircraft?.model) && ' · '}
							{[aircraft?.manufacturer, aircraft?.model].filter(Boolean).join(' ')}
							{' · Fuel Tank'}
						</p>
					</div>
				</div>
				<div className="self-end md:self-auto">
					<Button variant="light" isIconOnly onPress={handleBack}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			{/* Fuel Tank Model Card */}
			{/* Fuel Tank Model Name — read-only, same as legacy (disabled in tpl-fuel-tank.twig:33) */}
			{tankModel && (
				<Card className="mb-6">
					<CardBody className="flex-row items-center gap-2 py-3">
						<Fuel className="w-5 h-5 text-default-400" />
						<span className="text-sm font-medium text-default-700">{tankModel}</span>
					</CardBody>
				</Card>
			)}

			{/* Fuel Tank Graph */}
			<div className="mb-6">
				<div className="flex justify-between items-center mb-4">
					<h2 className="text-lg font-semibold">Fuel Tank Graph</h2>
					<div className="flex gap-2">
						<input
							ref={fileInputRef}
							type="file"
							accept=".csv,.xls,.xlsx"
							onChange={handleFileUpload}
							className="hidden"
						/>
						{canManage && <Button
							size="sm"
							variant="flat"
							startContent={<Upload className="w-4 h-4" />}
							onPress={() => fileInputRef.current?.click()}
						>
							Upload File
						</Button>}
						{canManage && <Button
							size="sm"
							color="primary"
							variant="flat"
							startContent={<Plus className="w-4 h-4" />}
							onPress={handleAddRow}
						>
							Add Row
						</Button>}
					</div>
				</div>
					<p className="text-sm text-default-500 mb-4">
						Define fuel load percentages and their corresponding weights and arm positions.
						Upload a CSV file with 3 columns (Load %, Weight lbs, Arm in) or enter data manually.
					</p>

					{graphData.length === 0 ? (
						<div className="text-center py-8 text-default-500">
							<Fuel className="w-12 h-12 mx-auto mb-2 opacity-50" />
							<p>No fuel tank graph data</p>
							<p className="text-sm">Click "Add Row" to add data points</p>
						</div>
					) : (
						<div className="space-y-4">
							{/* Header */}
							<div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 text-sm font-medium text-default-500 pb-2 border-b border-default-200">
								<div>Fuel Load (%)</div>
								<div>Weight (lbs)</div>
								<div>Arm (in)</div>
								<div></div>
							</div>
							{/* Rows */}
							{graphData.map((row, index) => (
								<div key={index} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 items-center">
									<div>
										<Input
											size="sm"
											type="number"
											step="any"
											min="0"
											max="100"
											placeholder="0"
											value={row.load}
											isReadOnly={!canManage}
											onChange={e => handleGraphChange(index, 'load', e.target.value)}
										/>
									</div>
									<div>
										<Input
											size="sm"
											type="number"
											step="any"
											placeholder="0.00"
											value={row.weight}
											isReadOnly={!canManage}
											onChange={e => handleGraphChange(index, 'weight', e.target.value)}
										/>
									</div>
									<div>
										<Input
											size="sm"
											type="number"
											step="any"
											placeholder="0.00"
											value={row.arm}
											isReadOnly={!canManage}
											onChange={e => handleGraphChange(index, 'arm', e.target.value)}
										/>
									</div>
									{canManage && <div className="flex justify-center">
										<Button
											isIconOnly
											size="sm"
											color="danger"
											variant="light"
											onPress={() => handleRemoveRow(index)}
											isDisabled={graphData.length === 1}
										>
											<Trash2 className="w-4 h-4" />
										</Button>
									</div>}
								</div>
							))}
						</div>
					)}
			</div>

			{/* Action Buttons */}
			<div className="flex justify-end gap-3 sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-default-200 py-3 z-10">
				<Button variant="flat" onPress={handleBack}>
					Cancel
				</Button>
				{canManage && <Button
					color="primary"
					startContent={<Save className="w-4 h-4" />}
					onPress={handleSave}
					isLoading={saving}
				>
					Save Fuel Tank
				</Button>}
			</div>
		</div>
	);
}

function FuelTankPage() {
	return (
		<UnsavedChangesGuard>
			<FuelTankPageContent />
		</UnsavedChangesGuard>
	);
}

export default FuelTankPage;
